<template>
  <article :class="['product-card cart-type-aquarist flex flex-col rounded-xxl w-full sm:w-74 h-full bg-light overflow-hidden transition-all duration-200 hover:shadow transform hover:-translate-y-0.5 border border-card-border hover:border-card-border-hover', className, `product-card-${variant.id}`]">
    <div class="relative flex items-center justify-center cursor-pointer w-auto h-50" :aria-label="$t('text-product-image')">
      <span class="sr-only">{{ $t('text-product-image') }}</span>
      <a
        :href="translatePath(ROUTES.CODE.PRODUCT, slugify(variant?.id, variant?.name))"
        class="block overflow-hidden absolute top-0 left-0 bottom-0 right-0 box-border m-0 text-light bg-radial-gradient"
        :aria-label="variant.name"
        @click.prevent="handleProductQuickView"
      >
        <Image
          :src="variant?.image?.url || _.get(variant, 'images.0.url', null) || siteSettings.product.placeholderImage[config.public.layoutDesign]"
          :alt="variant.name"
          layout="fill"
          object-fit="cover"
          :width="294"
          :height="200"
          sizes="294px"
          class-name="product-image"
          :loading="loading"
        />
      </a>
      <div class="pointer-events-none absolute inset-0 card-image-linear-gradient" />
      <div class="absolute top-0 pt-2.5 px-5 w-full flex justify-between space-x-5">
        <div v-if="discount" class="rounded-xl text-xs leading-6 font-semibold bg-orange text-light" v-text="discount" />
        <Link
          v-else
          :href="translatePath(ROUTES.CODE.SUPPLIER, slugify(variant?.product?.supplier?.id, variant?.product?.supplier?.name))"
          :aria-label="variant?.product?.supplier?.name"
          class="flex flex-row space-x-1 items-center overflow-hidden cursor-pointer group"
        >
          <Avatar :src="variant?.product?.supplier?.logo" size="small" :name="variant?.product?.supplier?.name" :loading="loading" />
          <div class="flex flex-col w-full text-light font-bold text-xs group-hover:underline">
            <div>{{ supplierName?.first }}</div>
            <div>{{ supplierName?.last }}</div>
          </div>
        </Link>
        <div class="flex items-center justify-end gap-2">
          <VariantTopping :variant="variant" :accent="true" />
          <VariantFavorite v-if="_.has(variant, 'isInCustomerFavorites')" :variant="variant" :accent="true" />
        </div>
      </div>
      <div v-if="_.has(variant, 'isInCustomerLikes')" class="absolute bottom-4 start-5 flex flex-row items-center justify-start space-x-2.5">
        <VariantViews :variant="variant" :accent="true" />
        <VariantLikes :variant="variant" :accent="true" />
        <MessagesBadge :variant="variant" :accent="true" :on-click="openDetailsWithComments" />
      </div>
      <div
        v-if="variant?.top"
        v-tippy="toppingTooltip"
        class="absolute bottom-0 end-0 flex items-center justify-center cursor-pointer"
        @click="handleProductQuickView()"
      >
        <CornerTriangle class="w-17.5 h-17.5 text-product-type-topped-bg" />
        <div class="absolute uppercase text-2xs font-bold -rotate-45 top-5 left-7 w-17.5 text-product-type-topped-text">{{ $t('text-top') }}</div>
      </div>
      <div
        v-else-if="variant?.product?.type === PRODUCT_TYPE.WANTED"
        class="absolute bottom-0 end-0 flex items-center justify-center cursor-pointer"
        @click="handleProductQuickView()"
      >
        <CornerTriangle class="w-17.5 h-17.5 text-product-type-wanted-bg" />
        <div class="absolute uppercase text-2xs font-bold -rotate-45 top-7.5 left-4 w-17.5 text-product-type-wanted-text">{{ $t('text-request') }}</div>
      </div>
      <div
        v-else-if="variant?.product?.type === PRODUCT_TYPE.AUCTION"
        class="absolute bottom-0 end-0 flex items-center justify-center cursor-pointer"
        @click="handleProductQuickView()"
      >
        <CornerTriangle class="w-17.5 h-17.5 text-product-type-auction-bg" />
        <div class="absolute uppercase text-2xs font-bold -rotate-45 top-4.5 left-5 w-17.5 text-product-type-auction-text">{{ $t('text-auction') }}</div>
      </div>
    </div>
    <header class="flex flex-col justify-between px-5 pt-4 pb-5 grow">
      <div class="flex flex-col">
        <a
          :href="translatePath(ROUTES.CODE.PRODUCT, slugify(variant?.id, variant?.name))"
          :aria-label="variant.name"
          @click.prevent="handleProductQuickView"
        >
          <div :class="[ 'leading-tight text-lg text-primary-dark font-bold overflow-ellipsis overflow-hidden cursor-pointer mb-1 hover:primary-dark-hover hover:underline focus:primary-dark-hover focus:underline', variant?.product?.type === PRODUCT_TYPE.AUCTION ? 'line-clamp-1' : 'line-clamp-2' ]" v-text="variant.name" />
        </a>
        <div class="w-full mb-2 text-xs text-primary-dark font-normal line-clamp-1">
          <span v-if="variant?.publishedAt">{{ $luxon.fromISO(variant?.publishedAt).setLocale(routeLang.code.value).toRelative() }}</span>
          <span v-if="variant?.publishedAt" class="text-primary-light"> • </span>
          <span v-if="variant?.product?.supplier?.city"><span :class="variant?.product?.supplier?.latitude && variant?.product?.supplier?.longitude ? 'hover:underline cursor-pointer' : ''" @click="() => handleOpenLocation(variant?.product?.supplier)">{{ variant?.product?.supplier?.city }}</span>, </span>
          <span v-if="_.isNumber(props.variant?.product?.supplier?.distanceKm)">{{ variant?.product?.supplier?.distanceKm }} {{ $t('text-unit-km') }}</span>
          <span v-else v-tippy="store.hasCurrentLocation() ? null : $t('text-allow-geolocation')" class="cursor-pointer" @click="handleGetCoordinates">? {{ $t('text-unit-km') }}</span>
          <span class="text-primary-light"> • </span>
          <span v-if="variant?.product?.type === PRODUCT_TYPE.OFFER">{{ $t('text-offer')  }}</span>
          <span v-else-if="variant?.product?.type === PRODUCT_TYPE.WANTED">{{ $t('text-request') }}</span>
          <span v-else-if="variant?.product?.type === PRODUCT_TYPE.AUCTION">{{ $t('text-auction') }}</span>
        </div>
      </div>
      <div class="flex flex-col grow">
        <div v-if="variant?.product?.type === PRODUCT_TYPE.AUCTION" class="w-full text-xs text-primary-dark mb-1">
          {{ variant?.auction?.bidCount ? $t('text-bids-with-count', { count: variant?.auction?.bidCount }) : $t('text-bids-no-count') }}
        </div>
        <CountDown v-if="variant?.product?.type === PRODUCT_TYPE.AUCTION" class-name="text-xs mb-4" :status="variant?.auction?.status" :start-at="variant?.auction?.startAt" :finish-at="variant?.auction?.finishAt" />
      </div>
      <div class="flex flex-col mt-auto">
        <div class="w-full flex flex-wrap pb-2" :style="windowSize?.width?.value < 480 ? { width: 'calc(100vw - 80px)' } : {}">
          <FilterTagParameters :data="variant" :parameters="enums.eshopParameters" :highlighted="true" />
          <FilterTagCategory :data="variant" />
          <FilterTagParameters :data="variant" :parameters="enums.eshopParameters" />
        </div>
        <div class="flex items-center justify-between">
          <div class="flex items-center justify-start mr-2">
            <span class="text-base text-primary-dark font-bold" v-text="(auctionPrice || _.get(variant, 'price', null)) === null ? $t('text-negotiable') : (basePrice ? basePrice : price)" />
            <del v-if="discount" class="text-sm text-primary-light ms-2" v-text="discount" />
          </div>
          <RecommendProductBtn v-if="action === 'recommend'" :data="variant" />
          <ViewProductBtn v-else-if="action === 'view'" :variant="variant" />
          <EditProductBtn v-else-if="_.get(variant, 'product.supplier.id') === auth?.idSupplier" :data="variant.product" variant="simple" />
          <ViewProductBtn v-else-if="variant?.product?.type === PRODUCT_TYPE.AUCTION" :variant="variant" />
          <AddToCart v-else :data="variant" variant="simple" />
        </div>
      </div>
    </header>
  </article>
</template>

<script setup>
import _ from 'lodash-es';
import { ROUTES } from "@utils/routes";
import { PRODUCT_TYPE, STATUS } from "@utils/constants";
import { siteSettings } from "@settings/site.settings";
import { useAuthStore } from '@stores/auth';
import { useSelectedStore } from '@stores/selected';
import { useEnumStore } from '@stores/enum';
import { useUIStore } from '@stores/ui';
import usePrice from "@utils/use-price";
import Image from "@components/ui/image";
import AddToCart from "@components/product/add-to-cart/add-to-cart";
import EditProductBtn from "@components/product/product-edit/edit-product-btn";
import RecommendProductBtn from "@components/product/recommend-product-btn";
import ViewProductBtn from "@components/product/view-product-btn";
import VariantFavorite from "@components/product/product-details/variant-favorite";
import VariantTopping from "@components/product/product-details/variant-topping";
import VariantViews from "@components/product/product-details/variant-views";
import VariantLikes from "@components/product/product-details/variant-likes";
import MessagesBadge from "@components/common/messages-badge";
import FilterTagCategory from "@components/filter/filter-tag-category";
import FilterTagParameters from "@components/filter/filter-tag-parameters";
import CornerTriangle from "@components/icons/corner-triangle";
import Avatar from "@components/ui/avatar";
import Link from "@components/ui/link/link";
import CountDown from "@components/ui/countdown";

const props = defineProps({
  variant: {
    type: Object,
    required: true
  },
  action: {
    type: String,
    default: ''
  },
  className: {
    type: String,
    required: false,
    default: () => ''
  },
  loading: {
    type: String,
    default: null
  }
})

const config = useRuntimeConfig();
const auth = useAuthStore();
const store = useSelectedStore();
const enums = useEnumStore();
const ui = useUIStore();
const { t } = useI18n();
const { $eventBus, $toast, $luxon } = useNuxtApp();
const geolocation = useGeolocation();
const windowSize = useWindowSize();
const routeLang = useRouteLang();

const openAboutTop = (event, instance) => {
  const link = event.target.closest('[data-about-top]');
  if (!link) {
    return;
  }

  event.preventDefault();
  instance.hide();
  $eventBus.emit('sidebar:open', 'ABOUT_TOP');
};

const toppingTooltip = computed(() => {
  if (!props.variant?.top) {
    return '';
  }

  if (!auth.isLoggedIn || props.variant?.product?.supplier?.id !== auth.idSupplier) {
    return {
      content: `<a href="#about-top" data-about-top class="underline hover:no-underline">${t('text-want-to-top-your-ad')}</a>`,
      interactive: true,
      trigger: 'mouseenter',
      maxWidth: 260,
      placement: 'top',
      appendTo: () => document.body,
      onCreate(instance) {
        instance.popper.addEventListener('click', event => openAboutTop(event, instance));
      }
    };
  }

  if (!auth?.idSupplier || props.variant?.product?.supplier?.id !== auth.idSupplier || !props.variant?.topEndsAt) {
    return '';
  }

  const endsAt = $luxon.fromISO(props.variant.topEndsAt).setLocale(routeLang.code.value);
  if (!endsAt.isValid) {
    return '';
  }

  return t('text-topping-ends-at', { date: endsAt.toFormat('cccc d.M. H:mm') });
});

const auctionPrice = computed(() => _.get(props.variant, 'auction.currentBid', null) || _.get(props.variant, 'auction.startingBid', null));

const price = computed(() => usePrice({
  amount: auctionPrice.value || +props.variant?.price,
  baseAmount: auctionPrice.value || +props.variant?.sale_price,
  currencyCode: props.variant?.currency?.code
})?.price);

const basePrice = computed(() => usePrice({
  amount: auctionPrice.value || +props.variant?.price,
  baseAmount: auctionPrice.value || +props.variant?.sale_price,
  currencyCode: props.variant?.currency?.code
})?.basePrice);

const discount = computed(() => usePrice({
  amount: auctionPrice.value || +props.variant?.price,
  baseAmount: auctionPrice.value || +props.variant?.sale_price,
  currencyCode: props.variant?.currency?.code
})?.discount);

const supplierName = computed(() => {
  const str = props.variant?.product?.supplier?.name || '';
  const middleIndex = Math.floor(str.length / 2);
  let leftIndex = str.lastIndexOf(' ', middleIndex);
  let rightIndex = str.indexOf(' ', middleIndex + 1);
  if (rightIndex === -1) {
    rightIndex = str.length;
  }
  if (leftIndex === -1) {
    leftIndex = 0;
  }
  const useLeft = (middleIndex - leftIndex) < (rightIndex - middleIndex);
  const splitIndex = useLeft ? leftIndex : rightIndex;
  const first = _.trim(_.slice(str, 0, splitIndex).join(''));
  const last = _.trim(_.slice(str, splitIndex + 1).join(''));
  return { first, last };
});

const handleProductQuickView = async (action = null) => {
  if (ui.displayModal) {
    $eventBus.emit('modal:close');
  }
  nextTick(async () => {
    if (_.get(props.variant, 'product.status', STATUS.DRAFT) !== STATUS.ACTIVE) {
      await navigatorTo(translatePath(ROUTES.CODE.AD_EDIT, props.variant?.product?.id));
    } else {
      store.setAction(action);
      store.setProduct(props.variant);
      $eventBus.emit('modal:open', 'PRODUCT_DETAILS');
    }
  });
};

const openDetailsWithComments = () => handleProductQuickView('SELECT_TAB_COMMENTS');

const handleGetCoordinates = async () => {
  await geolocation.withLocation(async () => {
    await geolocation.getPlace();
    if (store.hasCurrentLocation()) {
      $toast.success(t('success.geolocation.accepted'));
      store.setLoadResetAll();
    } else {
      $toast.error(t('error.geolocation.denied'));
    }
  });
}

const handleOpenLocation = (supplier) => {
  if (supplier?.latitude && supplier?.longitude) {
    store.setMapLocation(supplier);
    $eventBus.emit('modal:open', 'LOCATION_MAP_MODAL');
  }
}

const auctionUpdate = (value) => {
  if (_.get(props.variant, 'auction.id') === value.id) {
    _.assignIn(props.variant.auction, value);
    store.setLoadResetOthers();
  }
};

onMounted(async () => {
  $eventBus.on('auction:update', auctionUpdate);
});

onUnmounted(() => {
  $eventBus.off('auction:update', auctionUpdate);
});

</script>
